import { t as localeText } from "@/shared/lib/translate";
import { useTranslation as useLocaleSubscription } from "react-i18next";
import { CaretLeftIcon, CaretRightIcon } from "./icons";
import { useId, useRef, type KeyboardEvent, type ReactNode } from "react";
import { cn } from "@/shared/lib/cn";
import { Button } from "./Button";
import { IconButton } from "./IconButton";

export interface TabItem<T extends string> {
  value: T;
  label: ReactNode;
  count?: number;
  disabled?: boolean;
  panelId?: string;
}

export interface TabsProps<T extends string> {
  value: T;
  items: TabItem<T>[];
  onChange: (value: T) => void;
  label: string;
  className?: string;
}

/** Automatic tabs: one tab stop, arrows select immediately, disabled tabs are skipped. */
export function Tabs<T extends string>({
  value,
  items,
  onChange,
  label,
  className,
}: TabsProps<T>) {
  const generatedId = useId();
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const selectedIndex = items.findIndex(
    (item) => item.value === value && !item.disabled,
  );
  const tabStopIndex =
    selectedIndex >= 0
      ? selectedIndex
      : items.findIndex((item) => !item.disabled);

  function move(event: KeyboardEvent<HTMLButtonElement>, from: number): void {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    const enabled = items
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => !item.disabled);
    if (!enabled.length) return;
    const current = enabled.findIndex(({ index }) => index === from);
    const target =
      event.key === "Home"
        ? enabled[0]
        : event.key === "End"
          ? enabled.at(-1)
          : enabled[
              (current +
                (event.key === "ArrowRight" ? 1 : -1) +
                enabled.length) %
                enabled.length
            ];
    if (!target) return;
    onChange(target.item.value);
    refs.current[target.index]?.focus();
    refs.current[target.index]?.scrollIntoView?.({ block: "nearest", inline: "nearest" });
  }

  return (
    <div
      role="tablist"
      aria-label={label}
      className={cn(
        "studio-tabs flex max-w-full items-center gap-1.5 overflow-x-auto",
        className,
      )}
    >
      {items.map((item, index) => {
        const active = item.value === value;
        const tabId = `${generatedId}-${item.value}`;
        return (
          <Button
            variant={active ? "secondary" : "ghost"}
            key={item.value}
            ref={(node) => {
              refs.current[index] = node;
            }}
            id={tabId}
            type="button"
            role="tab"
            aria-selected={active}
            aria-controls={item.panelId}
            disabled={item.disabled}
            tabIndex={index === tabStopIndex ? 0 : -1}
            className={cn(
              "shrink-0 gap-[7px] px-[15px] text-[13.5px]",
            )}
            onClick={() => onChange(item.value)}
            onKeyDown={(event) => move(event, index)}
          >
            {item.label}
            {item.count !== undefined ? (
              <span
                className={cn(
                  "inline-flex h-[18px] items-center rounded-pill px-[6px] text-[11px] font-semibold tabular-nums",
                  active
                    ? "bg-hover text-ink"
                    : "bg-canvas-soft text-ink-soft",
                )}
              >
                {item.count}
              </span>
            ) : null}
          </Button>
        );
      })}
    </div>
  );
}

export interface BreadcrumbItem {
  label: ReactNode;
  href?: string;
}

export function Breadcrumbs({
  items,
  label = "Breadcrumb",
  className,
}: {
  items: BreadcrumbItem[];
  label?: string;
  className?: string;
}) {
  return (
    <nav aria-label={label} className={className}>
      <ol className="m-0 flex min-w-0 list-none flex-wrap items-center gap-2 p-0 text-[13px]">
        {items.map((item, index) => {
          const current = index === items.length - 1;
          return (
            <li key={index} className="flex min-w-0 items-center gap-2">
              {index ? (
                <span aria-hidden="true" className="text-ink-faint">
                  <CaretRightIcon className="size-4" />
                </span>
              ) : null}
              {current || !item.href ? (
                <span
                  aria-current={current ? "page" : undefined}
                  className={cn(
                    "min-w-0 break-words",
                    current
                      ? "font-semibold text-ink"
                      : "font-medium text-ink-muted",
                  )}
                >
                  {item.label}
                </span>
              ) : (
                <a
                  href={item.href}
                  className="font-medium text-ink-muted no-underline hover:text-ink hover:underline hover:underline-offset-[3px]"
                >
                  {item.label}
                </a>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export interface PaginationProps {
  page: number;
  pages: number;
  onPageChange: (page: number) => void;
  totalItems?: number;
  pageSize?: number;
  label?: string;
  className?: string;
}

export function Pagination({
  page,
  pages,
  onPageChange,
  totalItems,
  pageSize = 24,
  label = "Pagination",
  className,
}: PaginationProps) {
  useLocaleSubscription();
  const start = totalItems ? (page - 1) * pageSize + 1 : undefined;
  const end = totalItems ? Math.min(page * pageSize, totalItems) : undefined;
  return (
    <nav
      aria-label={label}
      className={cn(
        "flex min-w-0 flex-wrap items-center justify-between gap-3",
        className,
      )}
    >
      <span
        role="status"
        aria-atomic="true"
        className="text-[12.5px] tabular-nums text-ink-soft"
      >
        {totalItems
          ? `${start} – ${end} of ${totalItems}`
          : localeText("interface.page_value0_of_value1_2283a9c7", { defaultValue: "Page {{value0}} of {{value1}}", value0: page, value1: pages })}
      </span>
      <div className="flex items-center gap-1">
        <PageButton
          label={localeText("interface.previous_page_81f54719", { defaultValue: "Previous page" })}
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          <CaretLeftIcon className="size-4" />
        </PageButton>
        <span
          className="inline-flex size-8 items-center justify-center rounded-sm bg-hover px-2 text-[13px] font-medium tabular-nums text-ink-soft"
          aria-current="page"
        >
          {page}
        </span>
        <PageButton
          label={localeText("interface.next_page_4bfc194b", { defaultValue: "Next page" })}
          disabled={page >= pages}
          onClick={() => onPageChange(page + 1)}
        >
          <CaretRightIcon className="size-4" />
        </PageButton>
      </div>
    </nav>
  );
}

function PageButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <IconButton
      size="sm"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="text-ink-soft"
    >
      {children}
    </IconButton>
  );
}
